import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney, fmtDateTime, groupIban } from "@/lib/format";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

function openSupportChat() {
  try {
    if (window.Tawk_API && typeof window.Tawk_API.showWidget === "function") {
      window.Tawk_API.showWidget();
    }
    if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
      window.Tawk_API.maximize();
    } else if (window.Tawk_API && typeof window.Tawk_API.toggle === "function") {
      window.Tawk_API.toggle();
    } else {
      // Tawk script not yet loaded — wait briefly and retry once
      setTimeout(() => {
        window.Tawk_API && window.Tawk_API.showWidget && window.Tawk_API.showWidget();
        window.Tawk_API && window.Tawk_API.maximize && window.Tawk_API.maximize();
      }, 600);
    }
  } catch {}
}

function SupportAlert({ settings: settingsProp, show, effective }) {
  const [settings, setSettings] = useState(settingsProp || null);
  useEffect(() => {
    if (settingsProp || effective) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/settings/public");
        if (alive) setSettings(data);
      } catch { /* fall back to defaults below */ }
    })();
    return () => { alive = false; };
  }, [settingsProp, effective]);

  // Effective alert (3-level resolution computed by the backend) takes precedence.
  let visible, title, message, showBtn;
  if (effective) {
    visible = !!effective.show;
    title = effective.title || "Aviso importante";
    message = effective.message || "";
    showBtn = effective.show_support_btn !== false;
  } else {
    visible = !!show && !(settings && settings.receipt_alert_enabled === false);
    title = (settings?.receipt_alert_title) || "Aviso importante";
    message = (settings?.receipt_alert_message) || "Ha sucedido un error con su transferencia. Por favor comuníquese al chat de soporte.";
    showBtn = settings ? settings.receipt_alert_show_support_btn !== false : true;
  }
  if (!visible) return null;
  return (
    <div className="mt-6 rounded-2xl border-2 p-4 flex items-start gap-3"
         style={{ borderColor: "#F59E0B", background: "linear-gradient(135deg,#FFFBEB,#FEF3C7)" }}
         data-testid="receipt-support-alert">
      <div className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center"
           style={{ background: "#F59E0B", color: "#fff" }}>
        <AlertTriangle size={20} />
      </div>
      <div className="text-[13px] leading-relaxed text-amber-900 flex-1">
        <div className="font-bold uppercase tracking-wider text-xs text-amber-800" data-testid="receipt-alert-title">{title}</div>
        <div className="mt-1 whitespace-pre-line" data-testid="receipt-alert-message">{message}</div>
        {showBtn && (
          <button
            type="button"
            onClick={openSupportChat}
            data-testid="alert-open-chat"
            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow"
            style={{ background: "linear-gradient(135deg,#F59E0B,#D97706)" }}
          >
            💬 Chat de soporte
          </button>
        )}
      </div>
    </div>
  );
}

/** Shared document chrome — header + footer with brand styling. */
function DocShell({ title, children }) {
  const { t, i18n } = useTranslation();
  const localeMap = { es: "es-ES", en: "en-GB", it: "it-IT", pt: "pt-PT" };
  return (
    <div className="bg-white text-slate-900 mx-auto p-5 sm:p-8 md:p-10 max-w-full overflow-x-hidden" style={{ maxWidth: 720 }}>
      <div className="flex items-start justify-between border-b-2 border-purple-600 pb-5">
        <div className="flex items-center gap-3">
          <BrandLogo size={40} />
        </div>
        <div className="text-right">
          <div className="font-mono-fin text-[10px] uppercase tracking-widest text-slate-500">{t("documents.preview_generated")}</div>
          <div className="font-mono-fin text-xs text-slate-700 mt-0.5">
            {new Date().toLocaleString(localeMap[i18n.language] || "es-ES", { dateStyle: "short", timeStyle: "short" })}
          </div>
        </div>
      </div>

      <h2 className="font-mono-fin text-[11px] uppercase tracking-[0.3em] text-slate-500 mt-7">{title}</h2>

      <div className="mt-3">{children}</div>

      <div className="mt-10 pt-5 border-t border-slate-200 text-[10.5px] text-slate-500 leading-relaxed">
        {t("documents.preview_footer")}
      </div>
    </div>
  );
}

function Row({ k, v, mono = false }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2.5">
      <dt className="text-slate-500 text-sm">{k}</dt>
      <dd className={`text-slate-900 text-sm text-right break-all ${mono ? "font-mono-fin" : ""}`}>{v ?? "—"}</dd>
    </div>
  );
}

/** Receipt preview rendered from the tx object — no PDF needed. */
export function ReceiptPreview({ tx, account, settings }) {
  const { t } = useTranslation();
  if (!tx) return null;
  const amount = Math.abs(tx.amount);
  const isCredit = tx.amount > 0;
  return (
    <DocShell title={t("documents.receipt_doc_title")}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
        <span className={`font-display font-black text-3xl sm:text-5xl tracking-tighter break-all ${isCredit ? "text-emerald-600" : "text-red-600"}`}>
          {isCredit ? "+" : "-"} {fmtMoney(amount, tx.currency)}
        </span>
      </div>
      <div className="text-slate-700 mt-1">{tx.description}</div>

      <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
           style={{ background: tx.status === "completed" ? "#DCFCE7" : "#DBEAFE", color: tx.status === "completed" ? "#15803D" : "#1D4ED8" }}>
        <span className="block w-1.5 h-1.5 rounded-full bg-current"></span>
        {tx.status === "completed" ? t("transfers.result_executed") : tx.status === "scheduled" ? t("transfers.result_scheduled") : tx.status}
      </div>

      <dl className="mt-6">
        <Row k={t("documents.row_reference")} v={tx.reference} mono />
        <Row k={t("documents.row_type")} v={tx.transfer_type ? t(`tx.type.${tx.transfer_type}`, { defaultValue: tx.transfer_type }) : "—"} />
        <Row k={t("documents.row_date")} v={fmtDateTime(tx.executed_at || tx.created_at)} />
        <Row k={t("documents.row_beneficiary")} v={tx.beneficiary_name || "—"} />
        <Row k={t("documents.row_destination")} v={tx.destination ? groupIban(tx.destination) : "—"} mono />
        {tx.destination_country && <Row k={t("documents.row_country")} v={tx.destination_country} mono />}
        <Row k={t("documents.row_concept")} v={tx.concept || "—"} />
        <Row k={t("documents.row_amount")} v={fmtMoney(amount, tx.currency)} mono />
      </dl>

      <SupportAlert settings={settings} show={!!tx.receipt_alert} effective={tx.effective_alert} />

      {tx.admin_note && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="receipt-admin-note">
          <div className="font-bold uppercase tracking-wider text-xs text-slate-500">{t("tx.drawer.note")}</div>
          <div className="mt-1 text-sm text-slate-800 whitespace-pre-line">{tx.admin_note}</div>
        </div>
      )}
    </DocShell>
  );
}

/** Account-titularity certificate preview. */
export function CertificatePreview({ account, user, blockchainHash }) {
  const { t } = useTranslation();
  if (!account) return null;
  return (
    <DocShell title={t("documents.cert_doc_title")}>
      <p className="text-slate-700 text-sm leading-relaxed">
        <span dangerouslySetInnerHTML={{ __html: t("documents.cert_doc_intro", { name: user?.full_name || "" }) }} />
      </p>

      <div className="mt-6 rounded-2xl p-5 text-white"
           style={{ background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #2563EB 100%)" }}>
        <div className="font-mono-fin text-[10px] uppercase tracking-widest text-white/70">{t("documents.cert_card_label")}</div>
        <div className="text-sm mt-1">{t("overview.account_number_chat")}</div>
        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
          <div><span className="text-white/70 font-mono-fin uppercase tracking-widest">{t("documents.row_bank")}</span><div>{account.bank_name || "OpenPayments S.A."}</div></div>
          <div><span className="text-white/70 font-mono-fin uppercase tracking-widest">{t("documents.row_balance")}</span><div className="font-mono-fin">{fmtMoney(account.balance, account.currency)}</div></div>
        </div>
      </div>

      <dl className="mt-6">
        <Row k={t("documents.row_holder")} v={user?.full_name} />
        <Row k={t("documents.row_email")} v={user?.email} mono />
        <Row k={t("documents.row_bank")} v={account.bank_name || "OpenPayments S.A."} />
        <Row k={t("documents.row_iban")} v={account.iban ? groupIban(account.iban) : "—"} mono />
        <Row k={t("documents.row_bic")} v={account.bic || "—"} mono />
        <Row k={t("documents.row_kyc")} v={(user?.kyc_status || "—").toUpperCase()} />
        <Row k={t("documents.row_currency")} v={account.currency} />
        <Row k={t("documents.row_opened")} v={(account.created_at || "").slice(0, 10)} mono />
      </dl>
    </DocShell>
  );
}
